import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, RefreshCw, Search } from 'lucide-react';
import {
  jumpstartSignupsApi,
  type JumpstartPendingSignup,
  type JumpstartSignupStatus,
} from '@/api/jumpstartSignups';
import { Badge, Button, EmptyState, Input, Loading, Select } from '@/components/ui';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'provisioned', label: 'Provisioned' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_BADGE: Record<JumpstartSignupStatus, 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'warning',
  paid: 'info',
  provisioned: 'success',
  failed: 'danger',
};

const ORG_BASE_URL = 'https://otomator-admin.pages.dev/orgs';

function formatDateTime(value: string | null): string {
  if (!value) return '-';

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function StatusBadge({ status }: { status: JumpstartSignupStatus }) {
  return <Badge variant={STATUS_BADGE[status] ?? 'neutral'}>{status}</Badge>;
}

function SumitTransactionCell({ signup }: { signup: JumpstartPendingSignup }) {
  if (!signup.sumit_transaction_id) return <span className="text-ink-muted">-</span>;

  const label = signup.sumit_document_number
    ? `${signup.sumit_transaction_id} / ${signup.sumit_document_number}`
    : signup.sumit_transaction_id;

  if (!signup.sumit_document_url) {
    return <span className="font-mono text-xs text-ink-secondary">{label}</span>;
  }

  return (
    <a
      href={signup.sumit_document_url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs text-info hover:underline"
    >
      {label}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function OrgLink({ orgId }: { orgId: string | null }) {
  if (!orgId) return <span className="text-ink-muted">-</span>;

  return (
    <a
      href={`${ORG_BASE_URL}/${orgId}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs text-info hover:underline"
    >
      {orgId}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function SignupRow({ signup }: { signup: JumpstartPendingSignup }) {
  return (
    <tr className="border-b border-navy-border last:border-0 hover:bg-navy-hover/60">
      <td className="px-4 py-3 text-xs text-ink-secondary whitespace-nowrap">
        {formatDateTime(signup.created_at)}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium text-ink-primary">{signup.business_name || '-'}</div>
      </td>
      <td className="px-4 py-3 text-sm text-ink-secondary">{signup.email}</td>
      <td className="px-4 py-3 text-sm text-ink-secondary">{signup.plan_slug || '-'}</td>
      <td className="px-4 py-3">
        <StatusBadge status={signup.status} />
      </td>
      <td className="px-4 py-3 text-xs text-ink-secondary whitespace-nowrap">
        {formatDateTime(signup.paid_at)}
      </td>
      <td className="px-4 py-3">
        <SumitTransactionCell signup={signup} />
      </td>
      <td className="px-4 py-3 max-w-56">
        <OrgLink orgId={signup.jumpstart_org_id} />
      </td>
      <td className="px-4 py-3 max-w-72 text-xs text-danger">
        <span className="line-clamp-2">{signup.error_message || '-'}</span>
      </td>
    </tr>
  );
}

export default function SignupStatusPage() {
  const [status, setStatus] = useState<JumpstartSignupStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  const {
    data: signups = [],
    error,
    isFetching,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['jumpstart-signups'],
    queryFn: jumpstartSignupsApi.listRecent,
    staleTime: 30_000,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return signups.filter((signup) => {
      if (status !== 'all' && signup.status !== status) return false;
      if (!q) return true;

      return (
        signup.email.toLowerCase().includes(q) ||
        (signup.business_name ?? '').toLowerCase().includes(q)
      );
    });
  }, [search, signups, status]);

  if (isLoading) {
    return <Loading text="Loading JumpStart signups..." />;
  }

  return (
    <div className="space-y-5" dir="ltr">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">JumpStart Signups</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Last 50 payment signups from the otomator project
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(220px,320px)_180px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search email or business"
            className="pl-9"
          />
        </div>
        <Select
          value={status}
          onChange={(event) => setStatus(event.target.value as JumpstartSignupStatus | 'all')}
          options={STATUS_OPTIONS}
          aria-label="Status"
        />
      </div>

      {error ? (
        <div className="rounded border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error instanceof Error ? error.message : 'Failed to load JumpStart signups'}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No signups match the current filters." />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1180px] text-left text-sm">
              <thead className="bg-navy/30">
                <tr className="border-b border-navy-border">
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Created</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Business</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Email</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Plan</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Paid</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">SUMIT Transaction</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">JumpStart Org</th>
                  <th className="px-4 py-3 text-xs font-semibold text-ink-muted">Error</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((signup) => (
                  <SignupRow key={signup.id} signup={signup} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
